import type {
  Placement,
  WorkerProfile,
  WorkerScenario,
  WorkerOrgScenario,
} from "@/components/ats-fbo/types"

// ---------------------------------------------------------------------------
// Return types
// ---------------------------------------------------------------------------

export interface ScenarioAndOrg {
  workerScenario: WorkerScenario
  workerOrgScenario: WorkerOrgScenario | null
}

// ---------------------------------------------------------------------------
// Pure derivation functions
// ---------------------------------------------------------------------------

/**
 * Derives the initial worker scenario from a placement and loaded profiles.
 * Called once after profiles load when no scenario has been set yet.
 *
 * - workerId set → find the matching profile → existing-profile
 * - workerContactId set (no workerId) → add-profile for that contact
 * - Neither set → null (no auto-match)
 */
export function deriveInitialScenario(
  placement: Pick<Placement, "workerId" | "workerContactId">,
  allProfiles: WorkerProfile[],
): ScenarioAndOrg | null {
  if (placement.workerId) {
    const profile = allProfiles.find((p) => p.id === placement.workerId)
    if (!profile) return null
    return {
      workerScenario: {
        kind: "existing-profile",
        profileId: profile.id,
        contactId: profile.contactId,
      },
      workerOrgScenario: profile.workerOrgId
        ? { kind: "existing", organizationId: profile.workerOrgId }
        : null,
    }
  }

  if (placement.workerContactId) {
    return {
      workerScenario: { kind: "add-profile", contactId: placement.workerContactId },
      workerOrgScenario: null,
    }
  }

  return null
}

/**
 * Derives the worker scenario when a contact is selected from the dropdown.
 *
 * - Contact has a profile matching the current profile type → existing-profile
 * - Contact has no matching profile → add-profile
 */
export function deriveScenarioOnContactSelect(
  contactId: string,
  profileTypeCode: string,
  allProfiles: WorkerProfile[],
): ScenarioAndOrg {
  const matchingProfile = allProfiles.find(
    (p) => p.contactId === contactId && p.profileTypeCode === profileTypeCode
  )

  if (matchingProfile) {
    return {
      workerScenario: {
        kind: "existing-profile",
        profileId: matchingProfile.id,
        contactId,
      },
      workerOrgScenario: matchingProfile.workerOrgId
        ? { kind: "existing", organizationId: matchingProfile.workerOrgId }
        : null,
    }
  }

  return {
    workerScenario: { kind: "add-profile", contactId },
    workerOrgScenario: null,
  }
}

/**
 * Derives the worker scenario when the profile type changes and a contact is
 * already selected. No-ops if current scenario is create-contact.
 *
 * - Contact has a profile with the new profile type → existing-profile + org
 * - Contact has no matching profile → add-profile, org cleared
 */
export function deriveScenarioOnProfileTypeChange(
  currentScenario: WorkerScenario,
  newProfileTypeCode: string,
  allProfiles: WorkerProfile[],
): ScenarioAndOrg {
  if (currentScenario.kind === "create-contact") {
    return { workerScenario: currentScenario, workerOrgScenario: null }
  }

  const contactId = currentScenario.contactId
  const matchingProfile = allProfiles.find(
    (p) => p.contactId === contactId && p.profileTypeCode === newProfileTypeCode
  )

  if (matchingProfile) {
    return {
      workerScenario: { kind: "existing-profile", profileId: matchingProfile.id, contactId },
      workerOrgScenario: matchingProfile.workerOrgId
        ? { kind: "existing", organizationId: matchingProfile.workerOrgId }
        : null,
    }
  }

  return {
    workerScenario: { kind: "add-profile", contactId },
    workerOrgScenario: null,
  }
}

/**
 * Auto-matches a worker scenario by profile type alone, when there is no
 * `placement.workerId`/`workerContactId` hint to drive `deriveInitialScenario`.
 * Used after the worker landscape loads for a freshly entered email.
 *
 * Returns a match only when exactly ONE profile in `allProfiles` matches the
 * given `profileTypeCode`. Multiple matches → null (the user picks which one,
 * since auto-picking the first would silently choose for them when several
 * are valid). Zero matches → null (the user picks or creates).
 */
export function deriveAutoMatchByProfileType(
  profileTypeCode: string,
  allProfiles: WorkerProfile[],
): ScenarioAndOrg | null {
  const matchingProfiles = allProfiles.filter(
    (p) => p.profileTypeCode === profileTypeCode
  )
  if (matchingProfiles.length !== 1) return null

  const [matched] = matchingProfiles
  return {
    workerScenario: {
      kind: "existing-profile",
      profileId: matched.id,
      contactId: matched.contactId,
    },
    workerOrgScenario: matched.workerOrgId
      ? { kind: "existing", organizationId: matched.workerOrgId }
      : null,
  }
}

/**
 * Derives the worker scenario when the worker org changes after a manual
 * unlock. Only called when a contact is selected.
 *
 * - An existing profile matches contact + profile type + new org → existing-profile
 * - No match → add-profile
 */
export function deriveScenarioOnOrgChange(
  selectedContactId: string,
  newOrgId: string,
  profileTypeCode: string,
  allProfiles: WorkerProfile[],
): WorkerScenario {
  const matchingProfile = allProfiles.find(
    (p) =>
      p.contactId === selectedContactId &&
      p.profileTypeCode === profileTypeCode &&
      p.workerOrgId === newOrgId
  )

  if (matchingProfile) {
    return {
      kind: "existing-profile",
      profileId: matchingProfile.id,
      contactId: matchingProfile.contactId,
    }
  }

  return { kind: "add-profile", contactId: selectedContactId }
}
